/**
 * Main menu for mobile devices
 *
 * @author bgr
 */
import { html, render, nothing } from "lit-html";
import { repeat } from "lit-html/directives/repeat.js";
import { data } from "@escapio/shared/state/payload";
import { langId } from "@escapio/shared/l10n/lang-id";
import { pageType } from "@escapio/portal/state/pageType";
import { translate } from "@escapio/shared/l10n/translate";
import { hasEnoughDeals } from "../../state/deals-flag";
import { track } from "../../tracker/EventTracker";
import {
	travelData,
	onChangeCurrency,
	updateCurrency,
} from "../../state/travelData";
import "../ClickTracker";
import "../header/DealsLink";
import { hasSignedUp } from "../newsletter/popupTracker";

class Menu extends HTMLElement {
	constructor() {
		super();
		this.context = this.getAttribute("context");
		this.render();
		onChangeCurrency(() => this.render());
	}

	render() {
		render(
			html`
				${hasEnoughDeals()
					? html`<div class="main-stripe main-menu__item">
							<esc-click-tracker
								category="${this.context}"
								action="Link geklickt"
								label="Deals - ${pageType}"
							>
								<esc-deals-link
									additional-classes="main-menu__link"
								></esc-deals-link>
							</esc-click-tracker>
						</div>`
					: nothing}
				<div class="main-stripe main-menu__item">
					<esc-click-tracker
						category="${this.context}"
						action="Link geklickt"
						label="Meine Merkliste - ${pageType}"
					>
						<esc-favorites-link
							always-with-title
							additional-classes="main-menu__link"
							hide-counter
						></esc-favorites-link>
					</esc-click-tracker>
				</div>
				<div class="main-stripe main-menu__item">
					<esc-click-tracker
						category="${this.context}"
						action="Link geklickt"
						label="Kontakt - ${pageType}"
						use-event-queue
					>
						<a
							class="main-menu__link"
							href="${data.uris["contact-frontend"]}"
							aria-label="${translate("basic_contact")}"
						>
							<i class="fa fa-question-circle" aria-hidden="true"></i
							><span class="menu-item__label"
								>${translate("basic_contact")}</span
							>
						</a>
					</esc-click-tracker>
				</div>
				<div class="main-stripe main-menu__item">
					<esc-click-tracker
						category="${this.context}"
						action="Link geklickt"
						label="Ueber uns - ${pageType}"
					>
						<a
							class="main-menu__link"
							href="${translate("main_menu_about_uri")}"
							target="_blank"
						>
							${translate("main_menu_about_label")}
						</a>
					</esc-click-tracker>
				</div>
				${this.renderBlogLink()}
				${["home", "article", "hotel"].includes(pageType) && !hasSignedUp()
					? html`<div class="main-stripe main-menu__item">
							<esc-click-tracker
								category="${this.context}"
								action="Link geklickt"
								label="Newsletter - ${pageType}"
								use-event-queue
							>
								<a
									class="main-menu__link"
									href="${data.uris["newsletter-subscription"]}"
								>
									${translate("main_menu_newsletter_label")}
								</a>
							</esc-click-tracker>
						</div>`
					: nothing}
				<div class="main-stripe main-menu__item">
					<esc-click-tracker
						category="${this.context}"
						action="Link geklickt"
						label="Impressum - ${pageType}"
					>
						<a
							class="main-menu__link"
							href="${translate("main_menu_imprint_uri")}"
							target="_blank"
						>
							${translate("main_menu_imprint_label")}
						</a>
					</esc-click-tracker>
				</div>
				<div class="main-stripe main-menu__item">
					<esc-click-tracker
						category="${this.context}"
						action="Link geklickt"
						label="Privatsphaere - ${pageType}"
					>
						<esc-privacy-settings
							class="main-menu__link"
						></esc-privacy-settings>
					</esc-click-tracker>
				</div>
				${this.renderLanguageSelector()} ${this.renderCurrencySelector()}
			`,
			this,
			{ host: this },
		);
	}

	renderLanguageSelector() {
		const languages = Object.entries(data.languageNavigation);
		if (!languages.length) {
			return nothing;
		}

		return html`
			<details
				class="main-stripe main-menu__item main-menu__details"
				@toggle=${event => {
					this.trackDetailsToggle("Sprache Dropdown", event.target.open);
				}}
			>
				<summary class="main-menu__link main-menu__details__summary">
					${translate("basic_language")}:
					${data.languageNavigation[langId].label}
					<span
						class="main-menu__details__summary__icon fa fa-angle-down"
					></span>
				</summary>
				<div class="main-menu__details__content">
					<ul class="language-selector">
						${repeat(
							languages,
							([id, language]) => html`
								<li class="language-selector__list-item">
									<span
										@click=${this.onLanguageChange(language.uri)}
										class=${id === langId
											? "language-selector__link language-selector__link--selected"
											: "language-selector__link"}
										>${language.label}</span
									>
								</li>
							`,
						)}
					</ul>
				</div>
			</details>
		`;
	}

	renderCurrencySelector() {
		const currencies = Object.entries(data.currencySelector);
		if (!currencies.length) {
			return nothing;
		}

		return html`
			<details
				class="main-stripe main-menu__item main-menu__details"
				@toggle=${event => {
					this.trackDetailsToggle("Waehrung Dropdown", event.target.open);
				}}
			>
				<summary class="main-menu__link main-menu__details__summary">
					${translate("basic_currency")}:
					${travelData.currency}${data.currencySelector[travelData.currency]
						? ` (${data.currencySelector[travelData.currency]})`
						: ""}
					<span
						class="main-menu__details__summary__icon fa fa-angle-down"
					></span>
				</summary>
				<div class="main-menu__details__content">
					<ul class="language-selector">
						${repeat(
							currencies,
							([id, symbol]) => html`
								<li class="currency-selector__list-item">
									<a
										href="#"
										class=${id === travelData.currency
											? "currency-selector__link currency-selector__link--selected"
											: "currency-selector__link"}
										@click=${event => {
											this.onClickCurrency(event, id);
										}}
										>${id}${symbol ? ` (${symbol})` : ""}</a
									>
								</li>
							`,
						)}
					</ul>
				</div>
			</details>
		`;
	}

	trackDetailsToggle(element, open) {
		const action = open ? "öffnen" : "schließen";
		track(this.context, element, `${action} - ${pageType}`);
	}

	onClickCurrency(event, id) {
		event.preventDefault();
		updateCurrency(id);
		this.querySelectorAll("details").forEach(detail => {
			detail.removeAttribute("open");
		});
	}

	onLanguageChange(target) {
		return () => (window.location = target);
	}

	renderBlogLink() {
		return langId !== "de"
			? nothing
			: html`
					<div class="main-stripe main-menu__item">
						<esc-click-tracker
							category="${this.context}"
							action="Link geklickt"
							label="Blog - ${pageType}"
						>
							<a class="main-menu__link" href="/blog" target="_blank"> Blog </a>
						</esc-click-tracker>
					</div>
				`;
	}
}

customElements.define("esc-menu", Menu);
